import type { CardConfig } from '../types';
import { pageContentOptions } from '../constants';
import { usePreviewTheme } from './PreviewTheme';
import CompanyIntroSection from './CompanyIntroSection';
import CoreBusinessSection from './CoreBusinessSection';
import PhilosophySection from './PhilosophySection';
import CompanyStrengthSection from './CompanyStrengthSection';
import CooperationSection from './CooperationSection';
import CompanyQualificationsSection from './CompanyQualificationsSection';
import ProductIntroSection from './ProductIntroSection';
import InvestmentProjectSection from './InvestmentProjectSection';
import CompanyPromoSection from './CompanyPromoSection';
import VisionSection from './VisionSection';

interface PageRendererProps {
  config: CardConfig;
}

function PagePlaceholder({ pageValue, label, labelEn }: { pageValue: string; label: string; labelEn?: string }) {
  const theme = usePreviewTheme();
  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold" style={{ color: theme.textHighlightColor }}>{label}</h3>
          {labelEn && <p className="text-xs text-gray-400 tracking-widest ml-1">{labelEn}</p>}
        </div>
      </div>
      <div className="p-8">
        <div className="text-xs text-gray-400 text-center py-6">
          【{label}】内容展示区域
        </div>
      </div>
    </div>
  );
}

export const PageRenderer: React.FC<PageRendererProps> = ({ config }) => {
  const currentPages = config.selectedPages || [];

  const renderPage = (pageValue: string) => {
    const option = pageContentOptions.find(o => o.value === pageValue);
    if (!option) return null;

    const wrapperClass = "mt-4 bg-white rounded-xl overflow-hidden shadow-sm";

    switch (pageValue) {
      case 'companyIntro': // 企业介绍
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.companyIntro ? (
              <CompanyIntroSection data={config.companyIntro} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'companyPhilosophy': // 企业理念
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.philosophyItems && config.philosophyItems.length > 0 ? (
              <PhilosophySection items={config.philosophyItems} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'companyQualifications':
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.companyQualifications && (
              (config.companyQualifications.honors?.length > 0) ||
              (config.companyQualifications.businessQualifications?.length > 0) ||
              (config.companyQualifications.strength?.length > 0) ||
              (config.companyQualifications.endorsement?.length > 0)
            ) ? (
              <CompanyQualificationsSection data={config.companyQualifications} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'coreBusiness': // 核心业务
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.coreBusinessItems && config.coreBusinessItems.length > 0 ? (
              <CoreBusinessSection items={config.coreBusinessItems} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'productIntro': // 产品介绍
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.productIntro && config.productIntro.length > 0 ? (
              <ProductIntroSection items={config.productIntro} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'investmentProjects': // 投资项目
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.investmentProjectItems && config.investmentProjectItems.length > 0 ? (
              <InvestmentProjectSection items={config.investmentProjectItems} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'companyStrength': // 企业优势
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.companyStrengthItems && config.companyStrengthItems.length > 0 ? (
              <CompanyStrengthSection items={config.companyStrengthItems} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'cooperation': // 合作关系
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.cooperation && (config.cooperation.count > 0 || (config.cooperation.logos && config.cooperation.logos.length > 0)) ? (
              <CooperationSection data={config.cooperation} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'companyVision': // 企业愿景
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.visionItems && config.visionItems.length > 0 ? (
              <VisionSection items={config.visionItems} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      case 'companyPromo': // 企业推广
        return (
          <div key={pageValue} className={wrapperClass}>
            {config.promoVideo ? (
              <CompanyPromoSection data={config.promoVideo} />
            ) : (
              <PagePlaceholder pageValue={pageValue} label={option.label} labelEn={option.labelEn} />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {currentPages.map(pageValue => renderPage(pageValue))}
    </>
  );
};

export default PageRenderer;
